import { signal } from '@preact/signals';

/**
 * Local app-lock (not a server credential): a convenience gate shown after
 * the app is already signed in (session persists via the refresh token,
 * ADR-0007). Protects against someone picking up an already-unlocked phone —
 * it does not replace the password, and is only as strong as the device's
 * own biometric/PIN, since WebAuthn here is used purely as a local presence
 * check with no server round-trip.
 */

const STORAGE_KEY = 'foyer.applock';

type StoredLock = {
  pinHash?: string;
  biometricCredentialId?: string; // base64url
};

function readStore(): StoredLock {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStore(v: StoredLock) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // Private browsing / storage disabled — app lock just won't persist,
    // not worth surfacing an error for a convenience feature.
  }
}

export const appLockEnabled = signal(!!(readStore().pinHash || readStore().biometricCredentialId));
/** True right after boot when app-lock is on; set false once unlocked for this session. */
export const isLocked = signal(appLockEnabled.value);

// Re-lock on backgrounding too, not just cold start — an installed PWA
// resuming from the background doesn't reload the page, so relying on
// module-init state alone would only ever lock once per install.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && appLockEnabled.value) {
      isLocked.value = true;
    }
  });
}

async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function toBase64Url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function fromBase64Url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(s.length / 4) * 4, '=');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}

export function hasBiometricSupport(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

export async function setPin(pin: string): Promise<void> {
  const store = readStore();
  store.pinHash = await sha256Hex(pin);
  writeStore(store);
  appLockEnabled.value = true;
}

export async function verifyPin(pin: string): Promise<boolean> {
  const store = readStore();
  if (!store.pinHash) return false;
  return (await sha256Hex(pin)) === store.pinHash;
}

export async function registerBiometric(): Promise<boolean> {
  try {
    const cred = (await navigator.credentials.create({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: { name: 'Foyer' },
        user: {
          id: crypto.getRandomValues(new Uint8Array(16)),
          name: 'app-lock',
          displayName: 'Foyer app lock',
        },
        pubKeyCredParams: [
          { type: 'public-key', alg: -7 }, // ES256
          { type: 'public-key', alg: -257 }, // RS256
        ],
        authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required' },
        timeout: 60000,
      },
    })) as PublicKeyCredential | null;
    if (!cred) return false;
    const store = readStore();
    store.biometricCredentialId = toBase64Url(cred.rawId);
    writeStore(store);
    appLockEnabled.value = true;
    return true;
  } catch {
    return false; // cancelled, unsupported, or no platform authenticator enrolled
  }
}

export async function verifyBiometric(): Promise<boolean> {
  const store = readStore();
  if (!store.biometricCredentialId) return false;
  try {
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: [{ id: fromBase64Url(store.biometricCredentialId), type: 'public-key' }],
        userVerification: 'required',
        timeout: 60000,
      },
    });
    return !!assertion; // resolving at all means the OS biometric/PIN check passed
  } catch {
    return false; // cancelled or failed
  }
}

export function hasPin(): boolean {
  return !!readStore().pinHash;
}
export function hasBiometricEnrolled(): boolean {
  return !!readStore().biometricCredentialId;
}

export function disableAppLock(): void {
  writeStore({});
  appLockEnabled.value = false;
  isLocked.value = false;
}

export function unlock(): void {
  isLocked.value = false;
}
